import Big from 'big.js';

import type { DiscountCalculationMethod } from 'src/gql/graphql';
import { Product } from 'types/graphql-overrides';

import useCart from 'src/hooks/use-cart';
import { useCartPricing } from 'src/hooks/use-cart-pricing/use-cart-pricing';
import { usePriceCartCalcs } from 'src/hooks/use-price-cart-calcs';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';

import CartItemClass from 'src/state/cart-item';

import { hasSaleSpecialForMenuType, productSatisfiesSaleWeight } from 'shared/helpers/specials';

type UseCartItemDiscountReturn = {
  originalPrice: string;
  bogoDiscountedPrice: string | null;
  adjustedPrice: string;
  hideDiscounts: boolean;
  isWeightOnSale: boolean;
  hasDiscount: boolean;
  showOriginalPrice: boolean;
  showDiscountedPrice: boolean;
  calculationMethod: DiscountCalculationMethod | null;
};

export const useCartItemDiscount = (item: CartItemClass): UseCartItemDiscountReturn => {
  const { getItemPrice } = useCartPricing();
  const { originalPrice, adjustedPrice, bogoDiscountedPrice, calculationMethod } = getItemPrice(item);
  const { dispensary } = useCart();
  const hideDiscounts = useHideDiscountFromConsumer(dispensary);
  const isPriceCartEnabled = usePriceCartCalcs();
  const { product, cart, option } = item;
  const isMedical = cart?.isMedical;
  const weight = option || 'N/A';

  const isWeightOnSale = cartItemHasDiscount({
    product,
    weight,
    adjustedPrice,
    originalPrice,
    isMedical,
    onlyComparePrices: isPriceCartEnabled,
  });

  const hasDiscount = !hideDiscounts && (!!bogoDiscountedPrice || !!isWeightOnSale);
  const showOriginalPrice = !hideDiscounts || (!bogoDiscountedPrice && !isWeightOnSale);
  const showDiscountedPrice = !hideDiscounts && (!!bogoDiscountedPrice || isWeightOnSale);

  return {
    originalPrice,
    bogoDiscountedPrice,
    adjustedPrice,
    hideDiscounts,
    isWeightOnSale,
    hasDiscount,
    showOriginalPrice,
    showDiscountedPrice,
    calculationMethod,
  };
};

export type CartItemHasDiscountProps = {
  product: Product;
  weight: string;
  adjustedPrice: string;
  originalPrice: string;
  isMedical: boolean;
  onlyComparePrices: boolean;
};

export const cartItemHasDiscount = ({
  product,
  weight,
  adjustedPrice,
  originalPrice,
  isMedical,
  onlyComparePrices,
}: CartItemHasDiscountProps): boolean => {
  if (onlyComparePrices) {
    return Number(adjustedPrice || originalPrice) < Number(originalPrice);
  }
  const saleSpecials = product.specialData?.saleSpecials;
  const isOnSale =
    Big(adjustedPrice).lt(Big(originalPrice)) &&
    hasSaleSpecialForMenuType({ product, medicalOrder: isMedical || false });

  return isOnSale && !!saleSpecials ? productSatisfiesSaleWeight(product, saleSpecials, weight) : false;
};
